<template>
  <table class="analytics-table">
    <thead class="analytics-table--thead">
    <tr>
      <th v-if="debug"></th>
      <th v-if="show_title" class="analytics-table--left-aligned"></th>
      <th v-if="group_id" class="analytics-table--left-aligned">Date</th>
      <th v-if="secondary_group_id" class="analytics-table--left-aligned">{{ group_headline(secondary_group_id) }}</th>
      <th v-if="tertiary_group_id" class="analytics-table--left-aligned">{{ group_headline(tertiary_group_id) }}</th>

      <th v-if="channel_id && secondary_group_id !== 'channel_id' && tertiary_group_id !== 'channel_id'">Channel</th>
      <th v-if="platform_id && secondary_group_id !== 'platform_id' && tertiary_group_id !== 'platform_id'">Platform</th>
      <th v-if="manager_id && secondary_group_id !== 'manager_id' && tertiary_group_id !== 'manager_id'">Manager</th>
      <th v-if="team_id && secondary_group_id !== 'team_id' && tertiary_group_id !== 'team_id'">Team</th>
      <th v-if="customer_id && secondary_group_id !== 'customer_id' && tertiary_group_id !== 'customer_id'">Customer</th>
      <th v-if="customer_type_id && secondary_group_id !== 'customer_type_id' && tertiary_group_id !== 'customer_type_id'">Customer type</th>
      <th v-if="advertiser_id && secondary_group_id !== 'advertiser_id' && tertiary_group_id !== 'advertiser_id'">Advertiser</th>
      <th v-if="publisher_id && secondary_group_id !== 'publisher_id' && tertiary_group_id !== 'publisher_id'">Publisher</th>
      <th v-if="turnover_group_id && secondary_group_id !== 'turnover_group_id' && tertiary_group_id !== 'turnover_group_id'">Turnover group</th>
      <th v-if="media_planning_id && secondary_group_id !== 'media_planning_id' && tertiary_group_id !== 'media_planning_id'">Media plan</th>
      <th v-if="campaign_id && secondary_group_id !== 'campaign_id' && tertiary_group_id !== 'campaign_id'">Campaign</th>

      <th v-if="count_media_plannings">Media plans</th>
      <th v-if="count_campaigns">Campaigns</th>

      <th v-if="show_commercial_insights">Turnover</th>
      <th v-if="show_commercial_insights">Costs</th>
      <th v-if="show_commercial_insights">Margin</th>
      <th v-if="show_commercial_insights">Margin&nbsp;%</th>

      <th v-if="show_cost_metrics"><abbr title="Cost per mille">CPM</abbr></th>
      <th v-if="show_cost_metrics"><abbr title="Cost per click">CPC</abbr></th>
      <th v-if="show_cost_metrics"><abbr title="Cost per conversion">CPO</abbr></th>

      <th v-if="show_performance_metrics"><abbr title="Impressions">Imps</abbr></th>
      <th v-if="show_performance_metrics">Clicks</th>
      <th v-if="show_performance_metrics"><abbr title="Click through rate">CTR</abbr></th>
      <th v-if="show_performance_metrics">Complete</th>
      <th v-if="show_performance_metrics"><abbr title="Complete rate">CR</abbr></th>
      <th v-if="show_performance_metrics"><abbr title="Video views">VV</abbr></th>
      <th v-if="show_performance_metrics"><abbr title="Video view rate">VVR</abbr></th>
      <th v-if="show_performance_metrics"><abbr title="Conversions">CONVS</abbr></th>
      <th v-if="show_performance_metrics"><abbr title="Conversion rate">CVR</abbr></th>
      <th v-if="show_performance_metrics"><abbr title="Conversion value">Conv&nbsp;value</abbr></th>

      <th v-if="show_time_tracking_metrics"><abbr title="Time tracking [h:mm]">TT</abbr></th>
      <th v-if="show_time_tracking_metrics"><abbr title="Setup time tracking [h:mm]">STT</abbr></th>
      <th v-if="show_time_tracking_metrics"><abbr title="Management time tracking [h:mm]">MTT</abbr></th>
      <th v-if="show_time_tracking_metrics"><abbr title="Reporting time tracking [h:mm]">RTT</abbr></th>
      <th v-if="show_time_tracking_metrics"><abbr title="Creative time tracking [h:mm]">CRTT</abbr></th>
      <th v-if="show_time_tracking_metrics"><abbr title="Offer time tracking [h:mm]">OTT</abbr></th>
      <th v-if="show_time_tracking_metrics"><abbr title="Communication time tracking [h:mm]">COTT</abbr></th>
    </tr>
    </thead>

    <tbody>
    <tr v-for="point in analytics">
      <td v-if="debug">
        {{ point }}
      </td>
      <td v-if="show_title" class="analytics-table--left-aligned">{{ point.title }}</td>
      <td v-if="group_id" class="analytics-table--left-aligned">
        {{ format_date_time(point._id) }}
      </td>
      <td v-if="secondary_group_id" class="analytics-table--left-aligned">
        <template v-if="secondary_group_id === 'turnover_group_id'">
          <turnover-group-info :turnover_groups="turnover_groups"
                               :turnover_group_id="point._id['turnover_group_id']"></turnover-group-info>
        </template>
        <template v-else>
          {{ group_title(secondary_group_id, point._id) }}
        </template>
      </td>
      <td v-if="tertiary_group_id" class="analytics-table--left-aligned">
        <template v-if="tertiary_group_id === 'turnover_group_id'">
          <turnover-group-info :turnover_groups="turnover_groups"
                               :turnover_group_id="point._id['turnover_group_id']"></turnover-group-info>
        </template>
        <template v-else>
          {{ group_title(tertiary_group_id, point._id) }}
        </template>
      </td>

      <td v-if="channel_id && secondary_group_id !== 'channel_id' && tertiary_group_id !== 'channel_id'">{{ channel_name(channel_id) }}</td>
      <td v-if="platform_id && secondary_group_id !== 'platform_id' && tertiary_group_id !== 'platform_id'">{{ platform_name(platform_id) }}</td>
      <td v-if="manager_id && secondary_group_id !== 'manager_id' && tertiary_group_id !== 'manager_id'">{{ manager_name(manager_id) }}</td>
      <td v-if="team_id && secondary_group_id !== 'team_id' && tertiary_group_id !== 'team_id'">{{ team_title(team_id) }}</td>
      <td v-if="customer_id && secondary_group_id !== 'customer_id' && tertiary_group_id !== 'customer_id'">{{ customer_title(customer_id) }}</td>
      <td v-if="customer_type_id && secondary_group_id !== 'customer_type_id' && tertiary_group_id !== 'customer_type_id'">
        {{ customer_type_title(customer_type_id) }}
      </td>
      <td v-if="advertiser_id && secondary_group_id !== 'advertiser_id' && tertiary_group_id !== 'advertiser_id'">{{ advertiser_title(advertiser_id) }}</td>
      <td v-if="publisher_id && secondary_group_id !== 'publisher_id' && tertiary_group_id !== 'publisher_id'">{{ publisher_title(publisher_id) }}</td>
      <td v-if="turnover_group_id && secondary_group_id !== 'turnover_group_id' && tertiary_group_id !== 'turnover_group_id'">
        <turnover-group-info turnover_groups="turnover_groups"
                             turnover_group_id="turnover_group_id"></turnover-group-info>
      </td>
      <td v-if="media_planning_id && secondary_group_id !== 'media_planning_id' && tertiary_group_id !== 'media_planning_id'">
        {{ media_planning_title(media_planning_id) }}
      </td>
      <td v-if="campaign_id && secondary_group_id !== 'campaign_id' && tertiary_group_id !== 'campaign_id'">
        {{ campaign_title(campaign_id) }}
      </td>

      <td v-if="count_media_plannings">{{ point.media_planning_count }}</td>
      <td v-if="count_campaigns">{{ point.campaign_count }}</td>

      <td v-if="show_commercial_insights">
        <vue-numeric v-model="point.turnover"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>

      <td v-if="show_commercial_insights">
        <euro-amount :value="point.cost"></euro-amount>
      </td>

      <td v-if="show_commercial_insights">
        <vue-numeric v-model="point.margin"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     v-bind:minus="true"
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>

      <td v-if="show_commercial_insights">
        <small v-if="point.margin_percent || point.margin_percent === 0.0" class="label secondary">
          <percent-rate :rate="point.margin_percent" :min_rate="0.3"></percent-rate>
        </small>
      </td>

      <td v-if="show_cost_metrics">
        <vue-numeric v-if="typeof point.cpm === 'number'"
                     v-model="point.cpm"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>

      <td v-if="show_cost_metrics">
        <vue-numeric v-if="typeof point.cpc === 'number'"
                     v-model="point.cpc"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>

      <td v-if="show_cost_metrics">
        <vue-numeric v-if="typeof point.cpo === 'number'"
                     v-model="point.cpo"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>

      <td v-if="show_performance_metrics">{{ point.impressions }}</td>
      <td v-if="show_performance_metrics">{{ point.clicks }}</td>
      <td v-if="show_performance_metrics">
        <percent-rate :rate="point.click_rate"></percent-rate>
      </td>
      <td v-if="show_performance_metrics">{{ point.complete }}</td>
      <td v-if="show_performance_metrics">
        <percent-rate :rate="point.complete_rate"></percent-rate>
      </td>

      <td v-if="show_performance_metrics">{{ point.video_views }}</td>
      <td v-if="show_performance_metrics">
        <percent-rate :rate="point.video_view_rate"></percent-rate>
      </td>

      <td v-if="show_performance_metrics">
        <vue-numeric v-if="point.conversions" v-model="point.conversions" separator="." v-bind:precision=2 v-bind:read-only=true></vue-numeric>
      </td>
      <td v-if="show_performance_metrics">
        <percent-rate :rate="point.conversion_rate"></percent-rate>
      </td>
      <td v-if="show_performance_metrics">
        <vue-numeric v-if="point.conversions_value"
                     v-model="point.conversions_value"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>

      <td v-if="show_time_tracking_metrics">{{ format_duration(point.time_tracking_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(point.time_tracking_setup_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(point.time_tracking_management_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(point.time_tracking_reporting_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(point.time_tracking_creative_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(point.time_tracking_offer_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(point.time_tracking_communication_duration) }}</td>
    </tr>
    </tbody>

    <tfoot v-if="summary.length" class="analytics-table--tfoot">
    <tr v-for="sum in summary">
      <td v-if="debug">
        {{ sum }}
      </td>
      <td class="analytics-table--left-aligned">Sum:</td>
      <td class="analytics-table--left-aligned" v-if="secondary_group_id"></td>
      <td class="analytics-table--left-aligned" v-if="tertiary_group_id"></td>

      <td v-if="channel_id && secondary_group_id !== 'channel_id' && tertiary_group_id !== 'channel_id'">{{ channel_name(channel_id) }}</td>
      <td v-if="platform_id && secondary_group_id !== 'platform_id' && tertiary_group_id !== 'platform_id'">{{ platform_name(platform_id) }}</td>
      <td v-if="manager_id && secondary_group_id !== 'manager_id' && tertiary_group_id !== 'manager_id'">{{ manager_name(manager_id) }}</td>
      <td v-if="team_id && secondary_group_id !== 'team_id' && tertiary_group_id !== 'team_id'">{{ team_title(team_id) }}</td>
      <td v-if="customer_id && secondary_group_id !== 'customer_id' && tertiary_group_id !== 'customer_id'">{{ customer_title(customer_id) }}</td>
      <td v-if="customer_type_id && secondary_group_id !== 'customer_type_id' && tertiary_group_id !== 'customer_type_id'">
        {{ customer_type_title(customer_type_id) }}
      </td>
      <td v-if="advertiser_id && secondary_group_id !== 'advertiser_id' && tertiary_group_id !== 'advertiser_id'">{{ advertiser_title(advertiser_id) }}</td>
      <td v-if="publisher_id && secondary_group_id !== 'publisher_id' && tertiary_group_id !== 'publisher_id'">{{ publisher_title(publisher_id) }}</td>
      <td v-if="turnover_group_id && secondary_group_id !== 'turnover_group_id' && tertiary_group_id !== 'turnover_group_id'">
        <turnover-group-info turnover_groups="turnover_groups"
                             turnover_group_id="turnover_group_id"></turnover-group-info>
      </td>
      <td v-if="media_planning_id && secondary_group_id !== 'media_planning_id' && tertiary_group_id !== 'media_planning_id'">
        {{ media_planning_title(media_planning_id) }}
      </td>
      <td v-if="campaign_id && secondary_group_id !== 'campaign_id' && tertiary_group_id !== 'campaign_id'">
        {{ campaign_title(campaign_id) }}
      </td>

      <td v-if="count_media_plannings">{{ sum.media_planning_count }}</td>
      <td v-if="count_campaigns">{{ sum.campaign_count }}</td>

      <td v-if="show_commercial_insights">
        <vue-numeric v-model="sum.turnover"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>
      <td v-if="show_commercial_insights">
        <euro-amount :value="sum.cost"></euro-amount>
      </td>
      <td v-if="show_commercial_insights">
        <vue-numeric v-model="sum.margin"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     v-bind:minus="true"
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>
      <td v-if="show_commercial_insights">
        <small v-if="typeof sum.margin_percent === 'number'" class="label secondary">
          <percent-rate :rate="sum.margin_percent" :min_rate="0.3"></percent-rate>
        </small>
      </td>

      <td v-if="show_cost_metrics">
        <vue-numeric v-if="typeof sum.cpm === 'number'"
                     v-model="sum.cpm"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>
      <td v-if="show_cost_metrics">
        <vue-numeric v-if="typeof sum.cpc === 'number'"
                     v-model="sum.cpc"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>
      <td v-if="show_cost_metrics">
        <vue-numeric v-if="typeof sum.cpo === 'number'"
                     v-model="sum.cpo"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>

      <td v-if="show_performance_metrics">{{ sum.impressions }}</td>

      <td v-if="show_performance_metrics">{{ sum.clicks }}</td>
      <td v-if="show_performance_metrics">
        <percent-rate :rate="sum.click_rate"></percent-rate>
      </td>

      <td v-if="show_performance_metrics">{{ sum.complete }}</td>
      <td v-if="show_performance_metrics">
        <percent-rate :rate="sum.complete_rate"></percent-rate>
      </td>

      <td v-if="show_performance_metrics">{{ sum.video_views }}</td>
      <td v-if="show_performance_metrics">
        <percent-rate :rate="sum.video_view_rate"></percent-rate>
      </td>

      <td v-if="show_performance_metrics">{{ sum.conversions }}</td>
      <td v-if="show_performance_metrics">
        <percent-rate :rate="sum.conversion_rate"></percent-rate>
      </td>
      <td v-if="show_performance_metrics">
        <vue-numeric v-if="sum.conversions_value"
                     v-model="sum.conversions_value"
                     separator="."
                     v-bind:precision=2
                     v-bind:read-only=true
                     currency="€"
                     currency-symbol-position="suffix">
        </vue-numeric>
      </td>

      <td v-if="show_time_tracking_metrics">{{ format_duration(sum.time_tracking_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(sum.time_tracking_setup_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(sum.time_tracking_management_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(sum.time_tracking_reporting_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(sum.time_tracking_creative_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(sum.time_tracking_offer_duration) }}</td>
      <td v-if="show_time_tracking_metrics">{{ format_duration(sum.time_tracking_communication_duration) }}</td>
    </tr>
    </tfoot>
  </table>
</template>

<script>
import "./analytics-table.scss"
import TurnoverGroupInfo from "../turnover-group-info/turnover-group-info-app"
import PercentRate from "../percent-rate/percent-rate.vue"
import EuroAmount from "../euro-amount/euro-amount.vue"
import VueNumeric from 'vue-numeric'
import moment from "moment"
import momentDurationFormatSetup from "moment-duration-format"
momentDurationFormatSetup(moment)

export default {
  name:       "analytics-table",
  props:      {
    group_id:               {
      required: false,
      default:  null,
    },
    secondary_group_id:     {
      required: false,
      default:  null,
    },
    tertiary_group_id:      {
      required: false,
      default:  null,
    },
    channel_id:             {
      required: false,
      default:  null,
    },
    platform_id:            {
      required: false,
      default:  null,
    },
    manager_id:             {
      required: false,
      default:  null,
    },
    team_id:                {
      required: false,
      default:  null,
    },
    customer_id:            {
      required: false,
      default:  null,
    },
    customer_type_id:       {
      required: false,
      default:  null,
    },
    advertiser_id:          {
      required: false,
      default:  null,
    },
    publisher_id:          {
      required: false,
      default:  null,
    },
    turnover_group_id:      {
      required: false,
      default:  null,
    },
    media_planning_id:      {
      required: false,
      default:  null,
    },
    campaign_id:            {
      required: false,
      default:  null,
    },
    channels:               {
      required: false,
      default:  function () {return []},
    },
    platforms:              {
      required: false,
      default:  function () {return []},
    },
    users:                  {
      required: false,
      default:  function () {return []},
    },
    teams:                  {
      required: false,
      default:  function () {return []},
    },
    customers:              {
      required: false,
      default:  function () {return []},
    },
    customer_types:         {
      required: false,
      default:  function () {return []},
    },
    advertisers:            {
      required: false,
      default:  function () {return []},
    },
    publishers:            {
      required: false,
      default:  function () {return []},
    },
    turnover_groups:        {
      required: false,
      default:  function () {return []},
    },
    media_plannings:        {
      required: false,
      default:  function () {return []},
    },
    campaigns:              {
      required: false,
      default:  function () {return []},
    },
    show_title:             {
      required: false,
      default:  false,
    },
    count_media_plannings:  {
      required: false,
      default:  false,
    },
    count_campaigns:        {
      required: false,
      default:  false,
    },
    show_commercial_insights:          {
      required: false,
      default:  false,
    },
    show_cost_metrics:      {
      required: false,
      default:  false,
    },
    show_performance_metrics:       {
      required: false,
      default:  false,
    },
    show_time_tracking_metrics:       {
      required: false,
      default:  false,
    },
    analytics:              {
      required: true,
    },
    summary:                {
      required: false,
      default:  function () {return []},
    },
    debug: {
      required: false,
      default:  false,
    },
  },
  components: {
    PercentRate,
    EuroAmount,
    TurnoverGroupInfo,
    VueNumeric,
  },
  data() {
    return {}
  },
  methods:  {
    format_date_time(time) {
      if (this.is_loading) return
      if (!time) return
      if (this.group_id === "date") {
        return moment(time[this.group_id]).format("L")
      }
      if (this.group_id === "week") {
        const week_string = `${time["year"]}W${time["week"].toString().padStart(2, "0")}`
        return moment(week_string).format("wo [CW] YYYY")
      }
      if (this.group_id === "month") {
        const month_string = `${time["year"]}-${time["month"]}`
        return moment(month_string).format("MMMM YYYY")
      }
      if (this.group_id === "year") {
        return time[this.group_id]
      }
    },
    group_title(group_id, point_id) {
      if (group_id === "channel_id") return this.channel_name(point_id[group_id])
      if (group_id === "platform_id") {
        if (point_id["dummy"]) return "Dummy"
        if (point_id["one_off_fee"]) return "One-off fee"
        if (point_id["time_tracking"]) return "Time tracking"
        return this.platform_name(point_id[group_id])
      }
      if (group_id === "manager_id") return this.manager_name(point_id[group_id])
      if (group_id === "team_id") return this.team_title(point_id[group_id])
      if (group_id === "customer_id") return this.customer_title(point_id[group_id])
      if (group_id === "customer_type_id") return this.customer_type_title(point_id[group_id])
      if (group_id === "advertiser_id") return this.advertiser_title(point_id[group_id])
      if (group_id === "publisher_id") return this.publisher_title(point_id[group_id])
      if (group_id === "media_planning_id") return this.media_planning_title(point_id[group_id])
      if (group_id === "campaign_id") return this.campaign_title(point_id[group_id])
    },
    channel_name(channel_id) {
      const c = this.channel(channel_id);
      if (c) return c.title
    },
    channel(channel_id) {
      return this.channels.find(channel => channel.id === channel_id)
    },
    platform_name(platform_id) {
      const platform = this.platforms.find(platform => platform.id === platform_id)
      if (platform) return platform.title
      return "Dummy / One-off fee / Time tracking"
    },
    manager_name(manager_id) {
      const manager = this.users.find(manager => manager.id === manager_id)
      if (manager) return manager.email
    },
    team_title(team_id) {
      const team = this.teams.find(team => team.id === team_id)
      if (team) return team.title
    },
    customer_title(customer_id) {
      const customer = this.customers.find(customer => customer.id === customer_id)
      if (customer) return customer.title
    },
    customer_type_title(customer_type_id) {
      const customer_type = this.customer_types.find(customer_type => customer_type.id === customer_type_id)
      if (customer_type) return customer_type.title
    },
    advertiser_title(advertiser_id) {
      const advertiser = this.advertisers.find(advertiser => advertiser.id === advertiser_id)
      if (advertiser) return advertiser.title
    },
    publisher_title(publisher_id) {
      const publisher = this.publishers.find(publisher => publisher.id === publisher_id)
      if (publisher) return publisher.title
    },
    media_planning_title(id) {
      const media_planning = this.media_plannings.find(media_planning => media_planning.id === id)
      if (media_planning) return media_planning.title
    },
    campaign_title(id) {
      const campaign = this.campaigns.find(campaign => campaign.id === id)
      if (campaign) return campaign.title
    },
    group_headline(group_id) {
      return {
        channel_id:        "Channel",
        platform_id:       "Platform",
        manager_id:        "Manager",
        team_id:           "Team",
        customer_id:       "Customer",
        customer_type_id:  "Customer type",
        advertiser_id:     "Advertiser",
        publisher_id:      "Publisher",
        turnover_group_id: "Turnover group",
        media_planning_id: "Media plan",
        campaign_id:       "Campaign",
      }[group_id]
    },
    format_duration(duration) {
      if (!duration) return
      return moment.duration(duration, "milliseconds").format("h:mm", { trim: false });
    },
  },
  computed: {
  },
}
</script>
